import { t, tf } from "./i18n.js";
import { starteBullOff } from "./bull-off.js";
// ==========================================
// CRICKET / TACTICS LOGIK
//
// Varianten (modular ueber CRICKET_VARIANTS erweiterbar):
//   cricket : 15, 16, 17, 18, 19, 20, Bull
//   tactics : 10 bis 20, Bull
//
// Wertung: Standard mit Punkten.
//   - Ein Feld ist "zu", wenn ein Spieler es 3x getroffen hat.
//   - Jeder weitere Treffer auf ein eigenes geschlossenes Feld gibt Punkte,
//     solange mindestens ein Gegner dieses Feld noch offen hat.
//   - Sieg: alle Felder zu UND Punkte >= alle Gegner.
//
// Modi: Solo-Training (1 Spieler) und 2 Spieler lokal.
// ==========================================

const CRICKET_VARIANTS = {
  cricket: {
    label: "Cricket",
    targets: [15, 16, 17, 18, 19, 20, 25],
  },
  tactics: {
    label: "Tactics",
    targets: [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 25],
  },
};

const CRICKET_MARKS_TO_CLOSE = 3;
const CRICKET_DARTS_PER_TURN = 3;

let cricketState = null;
let cricketHistoryStack = [];
let cricketCurrentMult = 1;

// ------------------------------------------------------------------
// SETUP
// ------------------------------------------------------------------

function openCricketSetup() {
  openGameSetup("cricket-setup-screen", "CRICKET", null, "cricket");
  const p1 = document.getElementById("cricket-p1-name");
  if (p1 && !p1.value) p1.value = myOnlineName || t("js.app.001");
  toggleCricketSoloFields();
}

function toggleCricketSoloFields() {
  const isSolo = document.getElementById("cricket-solo")?.checked;
  const p2Wrap = document.getElementById("cricket-p2-wrap");
  // Leerer String statt "block": Die Zeile ist im Stylesheet ein Flex-
  // Container. Ein hart gesetztes "block" wuerde Beschriftung und Feld
  // untereinander stellen, waehrend alle anderen Zeilen zweispaltig sind.
  if (p2Wrap) p2Wrap.style.display = isSolo ? "none" : "";
}

function startCricketGame() {
  const variantKey =
    document.getElementById("cricket-variant")?.value || "cricket";
  const variant = CRICKET_VARIANTS[variantKey];
  if (!variant) {
    showToast(t("js.cricket.005"), "error");
    return;
  }

  const isSolo = !!document.getElementById("cricket-solo")?.checked;
  const p1Name =
    document.getElementById("cricket-p1-name")?.value.trim() || t("js.app.001");
  const p2Name =
    document.getElementById("cricket-p2-name")?.value.trim() || t("js.app.002");

  if (!isSolo && p1Name.toLowerCase() === p2Name.toLowerCase()) {
    showToast(t("js.cricket.001"), "error");
    return;
  }

  const makePlayer = (name, userId) => ({
    name: name,
    user_id: userId,
    marks: Object.fromEntries(variant.targets.map((t) => [t, 0])),
    score: 0,
    darts: 0,
  });

  const hostId = !isGuest && currentUser ? currentUser.id : null;
  const gamePlayers = [makePlayer(p1Name, hostId)];
  if (!isSolo) gamePlayers.push(makePlayer(p2Name, null));

  cricketState = {
    variantKey: variantKey,
    variantLabel: variant.label,
    targets: [...variant.targets],
    players: gamePlayers,
    isSolo: isSolo,
    currentPlayer: 0,
    dartsThisTurn: [],
    isOver: false,
    winnerIndex: -1,
  };

  cricketHistoryStack = [];
  cricketCurrentMult = 1;

  const undoBtn = document.getElementById("btn-undo-cricket");
  if (undoBtn) undoBtn.style.display = "none";
  document.getElementById("cricket-setup-screen").style.display = "none";
  document.getElementById("game-cricket-screen").style.display = "block";

  setCricketMult(1);
  updateCricketUI();

  // Spielwache scharf (29.08.2026).
  spielwacheSpielGestartet("cricket", sichereCricketStand);

  // Bull-Off (15.08.2026). Im Solo-Training uebersprungen - da gibt es
  // niemanden auszulosen. Zuletzt aufgerufen, damit das Overlay ueber einem
  // fertig aufgebauten Bildschirm liegt.
  if (!isSolo && document.getElementById("chk-cricket-bulloff")?.checked) {
    starteBullOff({
      spieler: cricketState.players.map((p) => p.name),
      onFertig: (reihenfolge) => {
        cricketState.currentPlayer = reihenfolge[0];
        cricketState.dartsThisTurn = [];
        updateCricketUI();
      },
      onAbbruch: () => { /* Vorgabe bleibt: Spieler 1 beginnt */ },
    });
  }
}

// ── Spielwache: sichern & wiederherstellen (29.08.2026) ─────────────────
// cricketState traegt alles (Variante, Marks, wer dran ist); die Darts der
// LAUFENDEN Aufnahme werden wie beim Undo verworfen.
function sichereCricketStand() {
  return { state: cricketState };
}

function stelleCricketWieder(stand) {
  openCricketSetup();
  cricketState = JSON.parse(JSON.stringify(stand.state));
  cricketState.dartsThisTurn = [];
  cricketHistoryStack = [];
  cricketCurrentMult = 1;

  // Setup-Formular angleichen, damit Revanche/Setup danach stimmen.
  const variante = document.getElementById("cricket-variant");
  if (variante) variante.value = cricketState.variantKey;
  const solo = document.getElementById("cricket-solo");
  if (solo) solo.checked = !!cricketState.isSolo;
  if (typeof toggleCricketSoloFields === "function") toggleCricketSoloFields();
  const p1 = document.getElementById("cricket-p1-name");
  if (p1) p1.value = cricketState.players[0]?.name || "";
  const p2 = document.getElementById("cricket-p2-name");
  if (p2 && cricketState.players[1]) p2.value = cricketState.players[1].name;
  const undoBtn = document.getElementById("btn-undo-cricket");
  if (undoBtn) undoBtn.style.display = "none";

  document.getElementById("cricket-setup-screen").style.display = "none";
  document.getElementById("game-cricket-screen").style.display = "block";
  setCricketMult(1);
  updateCricketUI();
  spielwacheSpielGestartet("cricket", sichereCricketStand);
}

spielwacheRegistriereFortsetzen("cricket", "Cricket", stelleCricketWieder);

function cancelCricketGame() {
  cancelCurrentGame("game-cricket-screen");
}

function resetCricketState() {
  cricketState = null;
  cricketHistoryStack = [];
  cricketCurrentMult = 1;
}

// ------------------------------------------------------------------
// REGELN
// ------------------------------------------------------------------

function cricketIsClosedBy(player, target) {
  return player.marks[target] >= CRICKET_MARKS_TO_CLOSE;
}

function cricketAllClosed(player) {
  return cricketState.targets.every((t) => cricketIsClosedBy(player, t));
}

// Punkte gibt es nur, solange mindestens ein Gegner das Feld offen hat.
// Im Solo-Modus gibt es keine Gegner -> Ueberschuss zaehlt immer.
function cricketTargetScores(playerIndex, target) {
  if (cricketState.isSolo) return true;
  return cricketState.players.some(
    (p, i) => i !== playerIndex && !cricketIsClosedBy(p, target),
  );
}

function cricketMaxOpponentScore(playerIndex) {
  let max = 0;
  cricketState.players.forEach((p, i) => {
    if (i !== playerIndex && p.score > max) max = p.score;
  });
  return max;
}

function cricketCheckWin(playerIndex) {
  const p = cricketState.players[playerIndex];
  if (!cricketAllClosed(p)) return false;
  if (cricketState.isSolo) return true;
  return p.score >= cricketMaxOpponentScore(playerIndex);
}

// ------------------------------------------------------------------
// EINGABE
// ------------------------------------------------------------------

function setCricketMult(m) {
  cricketCurrentMult = m;
  [1, 2, 3].forEach((i) => {
    const btn = document.getElementById("cricket-mult-" + i);
    if (btn) btn.classList.toggle("active-mult", i === m);
  });
}

function cricketSnapshot() {
  cricketHistoryStack.push({
    state: JSON.parse(JSON.stringify(cricketState)),
    mult: cricketCurrentMult,
  });
  const undoBtn = document.getElementById("btn-undo-cricket");
  if (undoBtn) undoBtn.style.display = "block";
}

// Ein einzelner Dart. target = Zahl aus targets, mult = 1..3.
// Bull: mult 1 = Single Bull (1 Mark), mult 2 = Bullseye (2 Marks).
function cricketAddDart(target, mult = null) {
  if (!cricketState || cricketState.isOver) return;
  if (cricketState.dartsThisTurn.length >= CRICKET_DARTS_PER_TURN) return;
  if (!cricketState.targets.includes(target)) return;

  let multiplier = mult === null ? cricketCurrentMult : mult;
  // Auf das Bull gibt es kein Triple
  if (target === 25 && multiplier > 2) multiplier = 2;

  cricketSnapshot();

  const idx = cricketState.currentPlayer;
  const player = cricketState.players[idx];

  const missing = CRICKET_MARKS_TO_CLOSE - player.marks[target];
  const marksForClosing = Math.min(Math.max(missing, 0), multiplier);
  const excess = multiplier - marksForClosing;

  player.marks[target] += marksForClosing;

  let points = 0;
  if (excess > 0 && cricketTargetScores(idx, target)) {
    points = excess * target;
    player.score += points;
  }

  player.darts += 1;
  cricketState.dartsThisTurn.push({
    target: target,
    mult: multiplier,
    marks: marksForClosing,
    points: points,
  });

  setCricketMult(1);

  if (cricketCheckWin(idx)) {
    cricketState.isOver = true;
    cricketState.winnerIndex = idx;
    updateCricketUI();
    finishCricketGame();
    return;
  }

  if (cricketState.dartsThisTurn.length >= CRICKET_DARTS_PER_TURN) {
    cricketEndTurn(true);
    return;
  }

  updateCricketUI();
}

// Fehlwurf: verbraucht einen Dart ohne Wirkung.
function cricketAddMiss() {
  if (!cricketState || cricketState.isOver) return;
  if (cricketState.dartsThisTurn.length >= CRICKET_DARTS_PER_TURN) return;

  cricketSnapshot();
  cricketState.players[cricketState.currentPlayer].darts += 1;
  cricketState.dartsThisTurn.push({
    target: 0,
    mult: 0,
    marks: 0,
    points: 0,
  });
  setCricketMult(1);

  if (cricketState.dartsThisTurn.length >= CRICKET_DARTS_PER_TURN) {
    cricketEndTurn(true);
    return;
  }
  updateCricketUI();
}

// fromDartLimit = true, wenn der Zug automatisch nach 3 Darts endet.
function cricketEndTurn(fromDartLimit = false) {
  if (!cricketState || cricketState.isOver) return;
  if (!fromDartLimit) {
    if (cricketState.dartsThisTurn.length === 0) return;
    cricketSnapshot();
  }

  cricketState.dartsThisTurn = [];
  if (!cricketState.isSolo) {
    cricketState.currentPlayer =
      (cricketState.currentPlayer + 1) % cricketState.players.length;
  }
  setCricketMult(1);
  updateCricketUI();
}

function undoCricketTurn() {
  if (cricketHistoryStack.length === 0) return;
  const last = cricketHistoryStack.pop();
  cricketState = last.state;

  if (cricketHistoryStack.length === 0) {
    const undoBtn = document.getElementById("btn-undo-cricket");
    if (undoBtn) undoBtn.style.display = "none";
  }
  setCricketMult(last.mult);
  updateCricketUI();
}

// Tastatur: 1-3 Multiplikator, 0 Fehlwurf, Backspace Undo, Enter Zug beenden.
// Rueckgabe true = Taste wurde verarbeitet.
function handleCricketKey(key) {
  if (!cricketState || cricketState.isOver) return false;
  if (key === "0") {
    cricketAddMiss();
    return true;
  }
  if (["1", "2", "3"].includes(key)) {
    setCricketMult(parseInt(key));
    return true;
  }
  if (key === "Backspace") {
    undoCricketTurn();
    return true;
  }
  if (key === "Enter") {
    cricketEndTurn();
    return true;
  }
  return false;
}

// ------------------------------------------------------------------
// UI
// ------------------------------------------------------------------

function cricketMarkSymbol(count) {
  if (count <= 0) return "";
  if (count === 1) return "/";
  if (count === 2) return "X";
  return "⊗";
}

function cricketCell(text, kind) {
  const el = document.createElement("div");
  el.className = "cricket-cell cricket-cell-" + kind;
  el.textContent = text;
  return el;
}

function cricketSetText(id, value) {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

function updateCricketUI() {
  if (!cricketState) return;

  const targetLabel = (t) => (t === 25 ? "B" : String(t));
  const table = document.getElementById("cricket-table");
  if (table) {
    table.innerHTML = "";

    cricketState.targets.forEach((t) => {
      const row = document.createElement("div");
      row.className = "cricket-row";

      const p1Closed = cricketIsClosedBy(cricketState.players[0], t);
      const c1 = cricketCell(
        cricketMarkSymbol(cricketState.players[0].marks[t]),
        "marks",
      );
      if (p1Closed) c1.style.color = "var(--accent-green)";
      row.appendChild(c1);

      const btn = document.createElement("button");
      btn.className = "cricket-target-btn";
      btn.textContent = targetLabel(t);
      btn.onclick = () => cricketAddDart(t);
      btn.disabled = cricketState.isOver;
      row.appendChild(btn);

      if (!cricketState.isSolo) {
        const p2Closed = cricketIsClosedBy(cricketState.players[1], t);
        const c2 = cricketCell(
          cricketMarkSymbol(cricketState.players[1].marks[t]),
          "marks",
        );
        if (p2Closed) c2.style.color = "var(--accent-green)";
        row.appendChild(c2);
      } else {
        row.appendChild(cricketCell("", "marks"));
      }

      table.appendChild(row);
    });
  }

  cricketSetText("cricket-p1-score", cricketState.players[0].score);
  cricketSetText("cricket-p1-label", cricketState.players[0].name);

  const p2Box = document.getElementById("cricket-p2-box");
  if (cricketState.isSolo) {
    if (p2Box) p2Box.style.visibility = "hidden";
  } else {
    if (p2Box) p2Box.style.visibility = "visible";
    cricketSetText("cricket-p2-score", cricketState.players[1].score);
    cricketSetText("cricket-p2-label", cricketState.players[1].name);
  }

  const active = cricketState.players[cricketState.currentPlayer];
  cricketSetText(
    "cricket-turn-indicator",
    cricketState.isOver
      ? tf("js.cricket.gewinnt", {
      p1: cricketState.players[cricketState.winnerIndex].name,
    })
      : tf("js.cricket.003", { p1: active.name, p2: cricketState.dartsThisTurn.length, p3: CRICKET_DARTS_PER_TURN }),
  );

  cricketSetText("cricket-variant-label", cricketState.variantLabel);

  const dartsBox = document.getElementById("cricket-darts-display");
  if (dartsBox) {
    dartsBox.innerHTML = "";
    for (let i = 0; i < CRICKET_DARTS_PER_TURN; i++) {
      const d = cricketState.dartsThisTurn[i];
      const box = document.createElement("div");
      box.className = "cricket-dart-box";
      if (!d) {
        box.textContent = "-";
      } else if (d.mult === 0) {
        // Fehlwuerfe durchnummeriert - siehe game-501.js. Diese Schleife
        // zaehlt ab 0, deshalb i + 1. Hier wird kein gespeichertes label
        // verglichen (die Bedingung oben prueft d.mult === 0), die Aenderung
        // ist also folgenlos fuer die Spiellogik.
        box.textContent = "M" + (i + 1);
        box.style.color = "#888";
      } else {
        const prefix = d.mult === 2 ? "D" : d.mult === 3 ? "T" : "";
        box.textContent =
          d.target === 25
            ? d.mult === 2
              ? "B-EYE"
              : "BULL"
            : prefix + d.target;
        box.style.color =
          d.mult === 3
            ? "var(--accent-red)"
            : d.mult === 2
              ? "var(--accent-green)"
              : "white";
      }
      dartsBox.appendChild(box);
    }
  }

  const p1Box = document.getElementById("cricket-p1-box");
  if (p1Box)
    p1Box.classList.toggle("inactive", cricketState.currentPlayer !== 0);
  if (p2Box && !cricketState.isSolo)
    p2Box.classList.toggle("inactive", cricketState.currentPlayer !== 1);  // Nach jeder Standaenderung spiegeln (spielwache.js, 29.08.2026).
  if (typeof spielwacheTick === "function") spielwacheTick();
}

// ------------------------------------------------------------------
// ABSCHLUSS & STATS
// ------------------------------------------------------------------

function finishCricketGame() {
  const winner = cricketState.players[cricketState.winnerIndex];
  const isSolo = cricketState.isSolo;
  const dartsUsed = winner ? winner.darts : 0;
  const winnerName = winner ? winner.name : "";

  saveCricketStatsBackground();
  spielwacheSpielBeendet();

  // Gemeinsamer Endbildschirm statt Toast + goHome (Produkttest
  // 29.08.2026). Das Setup-Formular haelt seine Werte - Revanche startet
  // dieselbe Runde neu.
  zeigeEndbildschirm({
    screenId: "game-cricket-screen",
    titel: isSolo
      ? t("js.ende.geschafft")
      : tf("js.ende.sieg", { p1: winnerName }),
    untertitel: isSolo ? tf("js.cricket.004", { p1: dartsUsed }) : "",
    zeilen: isSolo
      ? []
      : [...cricketState.players]
          .sort((a, b) => b.score - a.score)
          .map((p) => ({ name: p.name, wert: p.score, aktiv: p === winner })),
    kennzahlen: [{ label: "Darts", wert: dartsUsed }],
    revanche: () => startCricketGame(),
    setup: () => openCricketSetup(),
  });
}

async function saveCricketStatsBackground() {
  if (!cricketState) return;
  try {
    const winner = cricketState.players[cricketState.winnerIndex];
    const payload = {
      variant: cricketState.variantKey,
      is_solo: cricketState.isSolo,
      winner_name: winner ? winner.name : null,
      details: cricketState.players.map((p) => ({
        name: p.name,
        score: p.score,
        darts: p.darts,
        marks: p.marks,
        is_win: p === winner,
      })),
    };

    if (typeof currentUser !== "undefined" && currentUser) {
      payload.user_id = currentUser.id;
    }

    const { error } = await _supabase.from("stats_cricket").insert([payload]);
    if (error) {
      console.error(t("js.cricket.002"), error.message);
    }
  } catch (err) {
    console.error(t("js.g121.002"), err);
  }
}

// ── Auto-Score-Adapter (autoscore.js, 13.08.2026) ──────────────────────────
//
// Cricket ist ein Pro-Dart-Modus: Jeder erkannte Dart geht sofort durch
// dieselben Funktionen wie die Handeingabe. Ein Treffer auf ein
// Cricket-Ziel wird mit seinem Multiplikator angeschrieben
// (cricketAddDart), alles andere ist ein Fehlwurf (cricketAddMiss) -
// auch ein Treffer auf eine Nicht-Ziel-Zahl, genau wie beim manuellen
// Zaehlen. Bullseye (base 50) ist das Doppel des Bulls (25, x2). Der Zug
// endet wie gehabt automatisch nach drei Darts (cricketEndTurn).
window.autoScoreAdapter = window.autoScoreAdapter || {};
window.autoScoreAdapter.cricket = {
  korrigierbar: false,
  aktiv: () =>
    document.getElementById("game-cricket-screen")?.style.display !== "none" &&
    !!cricketState &&
    !cricketState.isOver,
  ziel: () => document.getElementById("cricket-darts-display"),
  dart: (dart) => {
    if (cricketState.dartsThisTurn.length >= CRICKET_DARTS_PER_TURN)
      return null;
    const base = Number(dart?.base ?? 0);
    const mult = Number(dart?.mult ?? 1);
    if (base === 50) cricketAddDart(25, 2);
    else if (cricketState.targets.includes(base))
      cricketAddDart(base, Math.max(1, mult));
    else cricketAddMiss();
    return null;
  },
};

// ---------------------------------------------------------------
// Bruecke zu window: Inline-Handler im HTML und andere Module greifen
// weiterhin global auf diese Funktionen zu. Automatisch erzeugt beim
// Umstieg auf Vite - Modul-Scope ersetzt sonst die alten Globals.
// ---------------------------------------------------------------
Object.assign(window, {
  openCricketSetup,
  toggleCricketSoloFields,
  startCricketGame,
  cancelCricketGame,
  resetCricketState,
  setCricketMult,
  cricketAddMiss,
  cricketEndTurn,
  undoCricketTurn,
  handleCricketKey,
});
